import {Chip, useTheme} from "@mui/material";
import {Grid, Box} from "@mui/system";
import {containerNumberLength} from "global/constants";
import {useDebounceCallback} from "hooks/useDebounceCallback";
import pxToRem from "hooks/usePxToRem";
import {useEffect, useState} from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {
  GetEquipmentsList,
  GetPackageContainersList,
} from "services/api_v2/Processes/Processes.service";
import {CustomAutocomplete, CustomIconButton} from "ui";
import {AutoCompleteData} from "ui/CustomAutoComplete/CustomAutoComplete";
import {TypeItemLoad} from "util/globalEnums";
import useChangeDAteAndAutocomplete from "views/ProcessDetails/hooks/useChangeDAteAndAutocomplete";
import useHandleFieldLists from "views/ProcessDetails/hooks/useHandleFieldLists";
import {ProcessEquipment} from "views/ProcessDetails/Process.types";
import ProcessSelect from "views/ProcessDetails/refactor/components/ProcessSelect";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import {getTooltipText} from "views/ProcessDetails/utils/getTooltipText";
import {shouldDisableLclPackageOption} from "utils/lclPackageRestrictions";
import validateEquipmentIsFromProcess from "utils/validators/validateEquipmentIsFromProcess";
import validatePackageContainerIsAllowed from "utils/validators/validatePackageContainerIsAllowed";

type Props = {
  field: any;
  index: number;
  loadIndex: number;
  handleDeleteLogisticBillLading: (field: any, index: number) => Promise<void>;
  setHouseMeasurements: (shouldDirtyFields?: boolean) => void;
  cloneItemConfig: {
    icon: "content_copy" | "content_paste" | "close";
  };
  handleClickOnCloneItem: (index: number, valuesToClone: any) => void;
  isCloningItem: boolean;
  rootName: string;
};

type CargoItemLike = {
  number?: string | null;
  seal?: string | null;
  barSeal?: string | null;
  quantity?: number | null;
  packageContainer?: unknown;
  grossWeight?: number | null;
  measurement?: number | null;
  tare?: number | null;
  typeItemLoad?: string | null;
  typePackages?: unknown;
  dangerousGoodsClass?: string | null;
  dangerousGoodsCode?: string | null;
  netWeight?: number | null;
  equipment?: {id?: number | string | null; name?: string | null} | null;
};

export const isCargoItemPartiallyFilled = (item: CargoItemLike | undefined) => {
  if (!item) return false;
  const number = item.number ?? "";
  const seal = item.seal ?? "";
  const barSeal = item.barSeal ?? "";
  const quantity = item.quantity ?? 0;
  const netWeight = item.netWeight ?? 0;
  const grossWeight = item.grossWeight ?? 0;
  const measurement = item.measurement ?? 0;
  const tare = item.tare ?? 0;
  const typeItemLoad = item.typeItemLoad ?? "";
  const dangerousGoodsClass = item.dangerousGoodsClass ?? "";
  const dangerousGoodsCode = item.dangerousGoodsCode ?? "";

  const packageContainer = item.packageContainer;

  return (
    number !== "" ||
    seal !== "" ||
    barSeal !== "" ||
    quantity !== 0 ||
    netWeight !== 0 ||
    grossWeight !== 0 ||
    measurement !== 0 ||
    tare !== 0 ||
    (packageContainer !== null && packageContainer !== undefined) ||
    typeItemLoad !== "" ||
    dangerousGoodsClass !== "" ||
    dangerousGoodsCode !== ""
  );
};

export const validateEquipmentRequired = (
  item: CargoItemLike | undefined,
  typeLoad: string | undefined,
  equipment: CargoItemLike["equipment"],
  itemIndex: number,
) => {
  if (typeLoad !== "FCL") return true;

  const shouldRequire = isCargoItemPartiallyFilled(item);
  if (!shouldRequire) return true;

  const hasEquipment = Boolean(equipment?.id ?? equipment?.name);
  if (hasEquipment) return true;

  return `Item de carga ${itemIndex + 1} - Equipamento é obrigatório.`;
};

type ValidateEquipmentParams = {
  item: CargoItemLike | undefined;
  typeLoad: string | undefined;
  equipment: CargoItemLike["equipment"] | undefined;
  itemIndex: number;
  allowedEquipmentIds: Array<number | undefined>;
};

export const validateEquipment = ({
  item,
  typeLoad,
  equipment,
  itemIndex,
  allowedEquipmentIds,
}: ValidateEquipmentParams) => {
  const requiredResult = validateEquipmentRequired(
    item,
    typeLoad,
    equipment,
    itemIndex,
  );

  if (requiredResult !== true) return requiredResult;

  const normalizedEquipmentId = (() => {
    const id = equipment?.id;
    if (id === null || id === undefined) return id;
    if (typeof id === "number") return id;
    const parsed = Number(id);
    return Number.isNaN(parsed) ? null : parsed;
  })();

  return validateEquipmentIsFromProcess({
    equipment: equipment
      ? {
          id: normalizedEquipmentId,
          name: equipment.name ?? null,
        }
      : null,
    allowedEquipmentIds,
    itemIndex,
  });
};

const getNestedValue = (obj: unknown, path: string) => {
  if (!obj || typeof obj !== "object") return undefined;
  return path
    .split(".")
    .reduce<any>((acc, key) => (acc ? acc[key] : undefined), obj as any);
};

const Item = (props: Props) => {
  const {
    cloneItemConfig,
    isCloningItem,
    handleClickOnCloneItem,
    field,
    index,
    loadIndex,
    setHouseMeasurements,
    handleDeleteLogisticBillLading,
    rootName,
  } = props;

  const {
    control,
    setValue,
    getValues,
    clearErrors,
    formState: {errors},
    watch,
  } = useFormContext();
  const cancelledProcess =
    useWatch({name: "agencyingSituation"}) === "Cancelado";
  const destinationId = useWatch({
    name: "boarding.destination.id",
  }) as number | undefined;
  const currentTypeLoad = useWatch({
    name: `processLoads.${loadIndex}.typeLoad`,
  }) as string | undefined;

  const cargoItems: Array<{[key: number]: boolean}> = getValues(rootName);
  const [errorsState, setErrorsState] = useState(
    cargoItems?.reduce((acc, _, index) => ({...acc, [index]: false}), {}),
  );
  const [equipmentsOptions, setEquipmentsOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [search, setSearch] = useState<string>();

  const {
    getOptions: getEquipmentsOptions,
    onBottomScroll: onBottomScrollEquipments,
    isLoadingOptions: isLoadingEquipmentsOptions,
  } = useHandleFieldLists(GetEquipmentsList);

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  const setHouseMeasurementsDebounced = useDebounceCallback(
    setHouseMeasurements,
    1000,
  );

  const handleMakeTypeItemLoadRequired = () => {
    const item = getValues(`${rootName}.${index}`) as CargoItemLike | undefined;
    const partiallyFilled = isCargoItemPartiallyFilled(item);
    const hasEquipment = Boolean(item?.equipment?.id ?? item?.equipment?.name);
    return partiallyFilled || hasEquipment;
  };

  const getEquipmentsIds = () => {
    const equipments: ProcessEquipment[] = getValues("logisticEquipments");
    const idsList = equipments?.map((item) => item?.equipment?.id);
    return idsList;
  };

  const [equipmentsIdsList] =
    useState<(number | undefined)[]>(getEquipmentsIds());

  const equipmentsList: ProcessEquipment[] = useWatch({
    name: "logisticEquipments",
  });
  const getEquipmentsQuantity = () => {
    const totalOfEquipments = equipmentsList?.reduce(
      (acc, curr) => acc + curr?.quantity,
      0,
    );
    return totalOfEquipments;
  };

  useEffect(() => {
    getEquipmentsQuantity();
  }, []);

  const fieldIsRequired = (index: number) => {
    const number = getValues(`${rootName}.${index}.number`);
    const seal = getValues(`${rootName}.${index}.seal`);

    const equipment = getValues(`${rootName}.${index}.equipmentId`);
    const quantity = getValues(`${rootName}.${index}.quantity`);
    const netWeight = getValues(`${rootName}.${index}.netWeight`);
    const grossWeight = getValues(`${rootName}.${index}.grossWeight`);
    const measurement = getValues(`${rootName}.${index}.measurement`);
    const packageContainer = getValues(`${rootName}.${index}.packageContainer`);
    const typeItemLoad = getValues(`${rootName}.${index}.typeItemLoad`);

    const typePackages = getValues(`${rootName}.${index}.typePackages`);
    const dangerousGoodsClass = getValues(
      `${rootName}.${index}.dangerousGoodsClass`,
    );
    const dangerousGoodsCode = getValues(
      `${rootName}.${index}.dangerousGoodsCode`,
    );
    if (number !== "" && number !== null) {
      clearErrors(`processDate.N° do container`);
    }

    if (getValues(`${rootName}.${index}.id`)) {
      return true;
    }

    if (
      seal !== "" ||
      equipment !== undefined ||
      quantity !== 0 ||
      netWeight !== 0 ||
      grossWeight !== 0 ||
      measurement !== 0 ||
      packageContainer !== undefined ||
      typePackages !== null ||
      typeItemLoad !== "" ||
      dangerousGoodsClass !== "" ||
      dangerousGoodsCode !== ""
    ) {
      setErrorsState((prev) => ({...prev, [index]: true}));
      return true;
    }

    return false;
  };

  const [packagesOptions, setPackagesOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [searchPackages, setSearchPackages] = useState<string>();

  const {
    getOptions: getPackagesOptions,
    onBottomScroll: onBottomScrollPackages,
    isLoadingOptions: isLoadingPackagesOptions,
  } = useHandleFieldLists(GetPackageContainersList);

  const [
    extraSpaceWhenCargoIsNotDangerous,
    setExtraSpaceWhenCargoIsNotDangerous,
  ] = useState(0);

  const DANGEROUS_FIELD_SIZE = 1;
  const [isCargoDangerous] = useState(
    getValues(`processLoads[0].cargoDangerous`),
  );

  useEffect(() => {
    if (!isCargoDangerous) {
      setExtraSpaceWhenCargoIsNotDangerous((2 * DANGEROUS_FIELD_SIZE) / 10);
    }
  }, []);

  const theme = useTheme();

  const getTooltipTextAndIconColor = (icon) => {
    if (icon === "content_copy")
      return {text: "Copiar item", iconColor: theme.palette.text.primary};

    if (icon === "content_paste")
      return {
        text: "Colar item copiado aqui",
        iconColor: theme.palette.info[500],
      };

    if (icon === "close")
      return {text: "Encerrar clonagem", iconColor: theme.palette.error[500]};
    return {text: ""};
  };

  return (
    <Grid
      key={field.idForm}
      container
      columnSpacing={pxToRem(1)}
      rowSpacing={pxToRem(10)}
    >
      {isCloningItem && (
        <Grid size={0.3}>
          <CustomIconButton
            iconName={cloneItemConfig?.icon}
            onClick={() => {
              handleClickOnCloneItem(index, getValues(rootName)[index]);
              fieldIsRequired(index);
            }}
            tooltipText={getTooltipTextAndIconColor(cloneItemConfig.icon).text}
            sx={{
              ":hover": {
                color: getTooltipTextAndIconColor(cloneItemConfig.icon)
                  .iconColor,
              },
            }}
          />
        </Grid>
      )}
      <Grid size={0.4}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Chip label={index + 1} color="primary" size="small" />
        </Box>
      </Grid>

      <Grid
        size={1 + extraSpaceWhenCargoIsNotDangerous + (isCloningItem ? 0 : 0.3)}
      >
        <Controller
          name={`${rootName}.${index}.typeItemLoad`}
          control={control}
          render={({field: {onChange, value}}) => (
            <ProcessSelect
              data={Object.values(TypeItemLoad)}
              value={value ?? ""}
              name={`${rootName}.${index}.typeItemLoad`}
              onChange={(e) => {
                onChange(e);
                fieldIsRequired(index);
              }}
              size="small"
              fullWidth
              label="Tipo item"
              required={handleMakeTypeItemLoadRequired()}
            />
          )}
        />
      </Grid>

      <Grid size={1.3 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.number`}
          control={control}
          rules={{
            pattern: {
              value: /^[A-Za-z]{4}\d{7}$/,
              message: "Número do container. Usar formato: AAAA1111111.",
            },
            validate: (value) => {
              const isDuplicate = watch(rootName)?.some(
                (item, i) =>
                  i !== index &&
                  item.number.length === containerNumberLength &&
                  item.number === value,
              );

              return isDuplicate &&
                watch(`processLoads.${index}.typeLoad`) === "FCL"
                ? `Item de carga ${index + 1} - Número de container duplicado.`
                : true;
            },
          }}
          render={({field}) => (
            <ProcessTextField
              label="N° container"
              placeholder="N° container"
              error={
                errors.knowledges?.[1]?.logisticBillLading
                  ?.logisticBillLadingLoads?.[index]?.number
              }
              required={errorsState[index]}
              {...field}
            />
          )}
        />
      </Grid>

      <Grid size={1.1 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.seal`}
          control={control}
          render={({field: {value, onChange}}) => (
            <ProcessTextField
              name={`${rootName}.${index}.seal`}
              type="text"
              placeholder="Selo"
              value={value ?? ""}
              onChange={(e) => {
                onChange(e);
                fieldIsRequired(index);
              }}
              size="small"
              fullWidth
              label="Selo"
            />
          )}
        />
      </Grid>

      <Grid size={0.6 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.barSeal`}
          control={control}
          render={({field: {value, onChange}}) => (
            <ProcessTextField
              name={`${rootName}.${index}.barSeal`}
              type="text"
              placeholder="BarSeal"
              value={value ?? ""}
              onChange={(e) => {
                onChange(e);
                fieldIsRequired(index);
              }}
              size="small"
              fullWidth
              label="BarSeal"
            />
          )}
        />
      </Grid>

      <Grid size={1.4 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.equipment`}
          control={control}
          rules={{
            validate: (equipment) => {
              const typeLoad = getValues(
                `processLoads.${loadIndex}.typeLoad`,
              ) as string | undefined;
              const item = getValues(`${rootName}.${index}`) as
                | CargoItemLike
                | undefined;
              return validateEquipment({
                item,
                typeLoad,
                equipment,
                itemIndex: index,
                allowedEquipmentIds: equipmentsIdsList,
              });
            },
          }}
          render={({field: {value}, fieldState}) => {
            const typeLoad = getValues(`processLoads.${loadIndex}.typeLoad`) as
              | string
              | undefined;
            const item = getValues(`${rootName}.${index}`) as
              | CargoItemLike
              | undefined;
            const equipmentIsRequired =
              typeLoad === "FCL" && isCargoItemPartiallyFilled(item);
            const equipmentError =
              fieldState.error ??
              getNestedValue(errors, `${rootName}.${index}.equipment`);

            return (
              <CustomAutocomplete
                name={`${rootName}.${index}.equipment`}
                fullWidth
                textFieldProps={{
                  label: "Equipamento",
                  required: equipmentIsRequired,
                  error: Boolean(equipmentError),
                  helperText: equipmentError?.message ?? undefined,
                }}
                placeholder="Equipamento"
                tooltipText={getTooltipText(value?.name, 3)}
                loading={isLoadingEquipmentsOptions}
                options={mountOptions(value, equipmentsOptions)}
                value={value?.name}
                onOpen={() => getEquipmentsOptions(setEquipmentsOptions, 1, "")}
                onInputChange={(e, value) => {
                  if (e !== null) {
                    setSearch(value);
                    getEquipmentsOptions(setEquipmentsOptions, 1, search);
                  }
                }}
                getNextPage={() => {
                  onBottomScrollEquipments(setEquipmentsOptions, search);
                }}
                onChange={(e, value) => {
                  handleChangeAutocomplete(
                    e,
                    value,
                    `${rootName}.${index}.equipment`,
                  );
                  setSearch("");
                  setEquipmentsOptions([]);
                  fieldIsRequired(index);
                }}
                isOptionEqualToValue={(option, value) =>
                  value === option?.label
                }
                getOptionDisabled={(option) =>
                  !equipmentsIdsList?.includes(option?.id)
                }
              />
            );
          }}
        />
      </Grid>

      <Grid size={0.5 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.quantity`}
          control={control}
          render={({field: {value}}) => (
            <ProcessTextField
              name={`${rootName}.${index}.quantity`}
              type="number"
              placeholder="Quant."
              value={value ?? ""}
              onChange={(e) => {
                setValue(`${rootName}.${index}.quantity`, +e.target.value, {
                  shouldDirty: true,
                });
                setHouseMeasurementsDebounced();

                fieldIsRequired(index);
              }}
              size="small"
              inputProps={{
                min: 0.0,
                step: 1.0,
                onClick: (e) => value === 0 && e.currentTarget.select(),
              }}
              fullWidth
              label="Quantidade"
              required
            />
          )}
        />
      </Grid>
      <Grid size={1.6 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.packageContainer`}
          control={control}
          rules={{
            validate: (packageContainer) =>
              validatePackageContainerIsAllowed({
                packageContainer: packageContainer
                  ? {
                      id: packageContainer.id ?? null,
                      name: packageContainer.name ?? null,
                    }
                  : null,
                typeLoad: currentTypeLoad,
                destinationId,
                itemIndex: index,
              }),
          }}
          render={({field: {value}, fieldState}) => (
            <CustomAutocomplete
              fullWidth
              textFieldProps={{
                label: "Pacote",
                error: Boolean(fieldState.error),
                helperText: fieldState.error?.message ?? undefined,
              }}
              name={`${rootName}.${index}.packageContainer`}
              placeholder="Pacote"
              tooltipText={getTooltipText(value?.name, 3)}
              loading={isLoadingPackagesOptions}
              options={mountOptions(value, packagesOptions)}
              value={value?.name}
              onOpen={() => getPackagesOptions(setPackagesOptions, 1, "")}
              onInputChange={(e, value) => {
                if (e !== null) {
                  setSearchPackages(value);
                  getPackagesOptions(setPackagesOptions, 1, searchPackages);
                }
              }}
              getNextPage={() => {
                onBottomScrollPackages(setPackagesOptions, searchPackages);
              }}
              onChange={(e, value) => {
                handleChangeAutocomplete(
                  e,
                  value,
                  `${rootName}.${index}.packageContainer`,
                );
                setSearchPackages("");
                setPackagesOptions([]);
              }}
              isOptionEqualToValue={(option, value) => value === option?.label}
              getOptionDisabled={(option) =>
                shouldDisableLclPackageOption({
                  typeLoad: currentTypeLoad,
                  destinationId,
                  packageId: option?.id,
                })
              }
            />
          )}
        />
      </Grid>
      {/* Peso líquido oculto por enquanto 
           <CustomInputWrapper md={1.5} title="Peso líq.">
                <Controller
                  name={`logisticBillLadingLoads.${index}.netWeight`}
                  control={control}
                  render={({field: {value}}) => (
                    <CustomInput
                      name={`logisticBillLadingLoads.${index}.netWeight`}
                      type="number"
                      placeholder="Peso liq."
                      value={value ?? ""}
                      onChange={(e) => {
                        setValue(
                          `logisticBillLadingLoads.${index}.netWeight`,
                          +e.target.value,
                          {shouldDirty: true},
                        );
                        fieldIsRequired(index);
                      }}
                      size="small"
                      inputProps={{
                        style: {
                          fontSize: pxToRem(14),
                        },
                        min: 0.0,
                        step: 0.001,
                      }}
                    />
                  )}
                />
              </CustomInputWrapper> */}
      <Grid size={0.6 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.grossWeight`}
          control={control}
          render={({field: {value}}) => (
            <ProcessTextField
              fullWidth
              label="Peso bruto"
              name={`${rootName}.${index}.grossWeight`}
              type="number"
              placeholder="Peso bruto"
              value={value ?? ""}
              onChange={(e) => {
                setValue(`${rootName}.${index}.grossWeight`, +e.target.value, {
                  shouldDirty: true,
                });
                setHouseMeasurementsDebounced();
                fieldIsRequired(index);
              }}
              size="small"
              inputProps={{
                min: 0.0,
                step: 0.001,
                onClick: (e) => value === 0 && e.currentTarget.select(),
              }}
            />
          )}
        />
      </Grid>

      <Grid size={0.6 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.measurement`}
          control={control}
          render={({field: {value}}) => (
            <ProcessTextField
              fullWidth
              label="Medidas"
              name={`${rootName}.${index}.measurement`}
              type="number"
              placeholder="medidas"
              value={value ?? ""}
              onChange={(e) => {
                setValue(`${rootName}.${index}.measurement`, +e.target.value, {
                  shouldDirty: true,
                });

                setHouseMeasurementsDebounced();
                fieldIsRequired(index);
              }}
              size="small"
              inputProps={{
                min: 0.0,
                step: 0.001,
                onClick: (e) => value === 0 && e.currentTarget.select(),
              }}
            />
          )}
        />
      </Grid>

      <Grid size={0.3 + extraSpaceWhenCargoIsNotDangerous}>
        <Controller
          name={`${rootName}.${index}.tare`}
          control={control}
          render={({field: {value}}) => (
            <ProcessTextField
              fullWidth
              label="Tara"
              name={`${rootName}.${index}.tare`}
              type="number"
              placeholder="Tara"
              value={value}
              onChange={(e) => {
                setValue(`${rootName}.${index}.tare`, +e.target.value, {
                  shouldDirty: true,
                });
              }}
              size="small"
              inputProps={{
                min: 0.0,
                step: 0.001,
                onClick: (e) => value === 0 && e.currentTarget.select(),
              }}
            />
          )}
        />
      </Grid>

      {isCargoDangerous && (
        <>
          <Grid size={1 + extraSpaceWhenCargoIsNotDangerous}>
            <Controller
              name={`${rootName}.${index}.dangerousGoodsClass`}
              control={control}
              render={({field: {value, onChange}}) => (
                <ProcessTextField
                  name={`${rootName}.${index}.dangerousGoodsClass`}
                  type="text"
                  value={value ?? ""}
                  onChange={(e) => {
                    onChange(e);
                    fieldIsRequired(index);
                  }}
                  size="small"
                  fullWidth
                  label="Classe"
                />
              )}
            />
          </Grid>
          <Grid size={1}>
            <Controller
              name={`${rootName}.${index}.dangerousGoodsCode`}
              control={control}
              render={({field: {value, onChange}}) => (
                <ProcessTextField
                  name={`${rootName}.${index}.dangerousGoodsCode`}
                  type="text"
                  value={value ?? ""}
                  onChange={(e) => {
                    onChange(e);
                    fieldIsRequired(index);
                  }}
                  size="small"
                  fullWidth
                  label="Código"
                />
              )}
            />
          </Grid>
        </>
      )}

      <Grid
        size={0.2}
        display="flex"
        alignItems="end"
        justifyContent="flex-start"
      >
        <CustomIconButton
          iconName="delete"
          disabled={cancelledProcess || isCloningItem}
          sx={{
            pr: 0,
            ":hover": {
              color: "red",
            },
          }}
          onClick={() => {
            handleDeleteLogisticBillLading(field, index);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default Item;
